import { useState } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CreateSubscriptionDialog } from '@/components/subscriptions/create-subscription-dialog';
import { EmbeddedDataTable } from '@/components/data-table/embedded-data-table';
import { PERMISSIONS, usePerm } from '@/hooks/use-perm';
import { OrderDetail } from '@/lib/api/orders';
import { useEmbeddedSubscriptionsTableColumns } from './embedded-subscriptions-table-columns';

interface OrderSubscriptionsCardProps {
  order: OrderDetail;
}

export function OrderSubscriptionsCard({ order }: OrderSubscriptionsCardProps) {
  const { t } = useTranslation('orders');
  const { can } = usePerm();
  const columns = useEmbeddedSubscriptionsTableColumns();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  // Only a paid order carries the succeeded payment a subscription needs to rebill against.
  const canCreate = can(PERMISSIONS.SUBSCRIPTIONS_MANAGE) && order.status === 'paid' && !!order.storeId;

  // Rendered outside the empty-state branch on purpose: creating a subscription flips this
  // card out of that branch, which would unmount the dialog mid-flow and hide its results.
  const createDialog = canCreate ? (
    <CreateSubscriptionDialog
      orderIds={[order.id]}
      storeId={order.storeId!}
      open={isCreateOpen}
      onOpenChange={setIsCreateOpen}
    />
  ) : null;

  if (!order.subscriptions || order.subscriptions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('subscriptions')}</CardTitle>
          <CardDescription>{t('noSubscriptions')}</CardDescription>
        </CardHeader>
        {canCreate ? (
          <CardContent>
            <Button variant="outline" size="sm" onClick={() => setIsCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('createSubscription')}
            </Button>
          </CardContent>
        ) : null}
        {createDialog}
      </Card>
    );
  }

  // Map subscriptions to table format
  const subscriptionsData = order.subscriptions.map((subscription) => ({
    id: subscription.id,
    status: subscription.status,
    amount: (subscription as any).amount,
    currency: (subscription as any).currency || order.currency || 'USD',
    nextBillingDate: subscription.nextBillingDate,
    priceInterval: (subscription as any).priceInterval || 'month',
    priceIntervalCount: (subscription as any).priceIntervalCount || 1,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('subscriptions')}</CardTitle>
        <CardDescription>
          {order.subscriptions.length}{' '}
          {order.subscriptions.length !== 1 ? t('activeSubscriptions') : t('activeSubscription')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <EmbeddedDataTable
          columns={columns}
          data={subscriptionsData as any}
          emptyMessage={t('noSubscriptions')}
        />
      </CardContent>
      {createDialog}
    </Card>
  );
}
